import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Search } from "lucide-react";
import { FadeUp, MaskedLine } from "@/components/Reveal";
import { useContent } from "@/hooks/useContent";
import { HERO_IMG, PRODUCTS } from "@/data/products";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHAPTERS = [
  {
    num: "01",
    title: "The purity standard",
    body: "Every peptide we release is assayed at 99%+ purity by independent HPLC and mass spectrometry. Nothing ships without data. Nothing hides behind a label.",
  },
  {
    num: "02",
    title: "Verified, not claimed",
    body: "Batch-specific Certificates of Analysis from third-party laboratories accompany every order. The documentation is the product.",
  },
  {
    num: "03",
    title: "Research integrity",
    body: "We supply researchers, not consumers. Strict compliance, cold-chain logistics, and an uncompromising research-use-only policy define how we operate.",
  },
];

export default function Home() {
  const content = useContent();
  const navigate = useNavigate();
  const [heroQuery, setHeroQuery] = useState("");
  const [now, setNow] = useState(Date.now());
  const ANNOUNCE = {
    text: "The VetForge marketplace is live — independent vendor stores now open",
    link_label: "Shop the stores",
    link_url: "/vendors",
    countdown_to: "",
    ...(content?.announcement || {}),
  };
  const countdownTarget = ANNOUNCE.countdown_to ? Date.parse(ANNOUNCE.countdown_to) : null;
  useEffect(() => {
    if (!countdownTarget) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [countdownTarget]);
  const remain = countdownTarget ? Math.max(0, countdownTarget - now) : 0;
  const cd = {
    days: Math.floor(remain / 86400000),
    hours: Math.floor((remain % 86400000) / 3600000),
    minutes: Math.floor((remain % 3600000) / 60000),
    seconds: Math.floor((remain % 60000) / 1000),
  };
  const PILLARS = content?.pillars?.length ? content.pillars : [
    { title: "Vetted vendors", body: "Every marketplace store is reviewed for legitimacy, documentation, and reliability before it opens." },
    { title: "Open documentation", body: "Batch-specific COAs and testing data you can verify — the documentation travels with every order." },
    { title: "Member pricing", body: "Registered researchers unlock member-only prices across participating stores, applied automatically." },
    { title: "Veteran operated", body: "Service-first discipline: straight answers, documented product, and orders that ship when we say they ship." },
  ];
  const INSIDE = content?.inside_cards?.length ? content.inside_cards : [
    { title: "The marketplace", body: "Independent vetted stores under one roof — shop one store or fill one cart across all of them.", link_label: "Browse stores", link_url: "/marketplace" },
    { title: "Certificate of Analysis", body: "Every COA and batch record, organized in one place.", link_label: "Open the certificates", link_url: "/library" },
    { title: "Order tracking", body: "Live status from confirmation to tracking number, per store, in your account.", link_label: "Track an order", link_url: "/track" },
  ];
  const HERO = {
    overline: "VetForge Health & Wellness — Research Grade",
    line1: "Purity, verified.",
    line2: "Precision,",
    line3: "delivered.",
    subtext: "Research-grade peptides and genuine Hospira bacteriostatic water — independently tested, fully documented, and supplied exclusively for laboratory research.",
    ...(content?.hero || {}),
  };
  const SECTIONS = {
    featured_overline: "Selected compounds",
    featured_title: "The catalog, distilled",
    cta_title1: "Ready to begin",
    cta_title2: "your research?",
    cta_intro: "Submit an order inquiry and our team will respond within one business day with pricing, availability, and compliance documentation.",
    payment_label: "VetForge accepts",
    ...(content?.home_sections || {}),
  };
  const RUO = content?.footer?.ruo || "Research use only — Not for human consumption";
  const CHAPTERS_LIVE = content?.manifesto?.length ? content.manifesto : CHAPTERS;
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  const [featured, setFeatured] = useState([]);
  useEffect(() => {
    // Featured rail ranks by real shopper interest (product view counts)
    Promise.all([
      axios.get(`${API}/products`),
      axios.get(`${API}/products/popularity`).catch(() => ({ data: {} })),
    ]).then(([{ data: products }, { data: popularity }]) => {
      setFeatured(
        [...products]
          .sort((a, b) => (popularity[b.id] || 0) - (popularity[a.id] || 0))
          .slice(0, 3)
      );
    }).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {ANNOUNCE.text && (
        <div className="border-b border-[rgba(200,169,106,0.28)] bg-[#0D1322]" data-testid="announcement-bar">
          <div className="mx-auto max-w-7xl px-6 sm:px-10 py-3 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-center">
            <p className="text-[10px] uppercase tracking-[0.25em] text-clinical">{ANNOUNCE.text}</p>
            {countdownTarget && remain > 0 && (
              <span className="font-mono text-xs text-ink tabular-nums" data-testid="announcement-countdown">
                {cd.days}d : {String(cd.hours).padStart(2, "0")}h : {String(cd.minutes).padStart(2, "0")}m : {String(cd.seconds).padStart(2, "0")}s
              </span>
            )}
            {ANNOUNCE.link_url && (
              <Link to={ANNOUNCE.link_url} data-testid="announcement-link" className="text-[10px] uppercase tracking-[0.25em] text-[#B9BFCE] hover:text-clinical underline underline-offset-4 transition-colors duration-300">
                {ANNOUNCE.link_label || "Learn more"} →
              </Link>
            )}
          </div>
        </div>
      )}
      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 gold-glow pointer-events-none" aria-hidden="true" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[rgba(200,169,106,0.5)] to-transparent" aria-hidden="true" />
        <div className="mx-auto max-w-7xl px-6 sm:px-10 pt-24 pb-32 lg:pt-36 lg:pb-44 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center relative">
          <div className="lg:col-span-7 space-y-10">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="space-y-4"
            >
              <span className="dog-tag dog-tag-sm text-[10px]" data-testid="hero-veteran-badge">US Veteran Owned &amp; Operated</span>
              <p className="text-xs uppercase tracking-[0.35em] text-clinical">
                {HERO.overline}
              </p>
            </motion.div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] text-ink" data-testid="hero-headline">
              <MaskedLine delay={0.3}>{HERO.line1}</MaskedLine>
              <MaskedLine delay={0.45}>
                <span className="italic text-[#B9BFCE]">{HERO.line2}</span>
              </MaskedLine>
              <MaskedLine delay={0.6}>{HERO.line3}</MaskedLine>
            </h1>
            <FadeUp delay={0.8}>
              <p className="text-sm sm:text-base text-[#B9BFCE] leading-relaxed max-w-xl">
                {HERO.subtext}
              </p>
            </FadeUp>
            <FadeUp delay={0.9}>
              <form
                onSubmit={(e) => { e.preventDefault(); if (heroQuery.trim()) navigate(`/products?q=${encodeURIComponent(heroQuery.trim())}`); }}
                className="relative max-w-xl"
                data-testid="hero-search-form"
              >
                <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8E96AB] pointer-events-none" />
                <input
                  type="search"
                  value={heroQuery}
                  onChange={(e) => setHeroQuery(e.target.value)}
                  placeholder="Search the catalog — Tirzepatide, Semax, Bac Water…"
                  aria-label="Search products"
                  data-testid="hero-search"
                  className="w-full rounded-full border border-[#232B40] bg-paper/80 backdrop-blur pl-12 pr-28 py-3.5 text-sm text-ink placeholder:text-[#8E96AB] focus:outline-none focus:ring-1 focus:ring-clinical focus:border-clinical transition-colors duration-300"
                />
                <button
                  type="submit"
                  data-testid="hero-search-submit"
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-clinical text-[#0B101C] text-[10px] uppercase tracking-[0.2em] font-semibold rounded-full px-5 py-2 hover:bg-[#DBC28C] transition-colors duration-300"
                >
                  Search
                </button>
              </form>
            </FadeUp>
            <FadeUp delay={0.95} className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/products"
                data-testid="hero-catalog-button"
                className="group inline-flex items-center justify-center gap-3 bg-clinical text-[#0B101C] text-xs uppercase tracking-[0.2em] py-4 px-8 hover:bg-[#DBC28C] transition-colors duration-300"
              >
                Explore the catalog
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/library"
                data-testid="hero-quality-button"
                className="inline-flex items-center justify-center gap-3 border border-[rgba(200,169,106,0.5)] text-ink text-xs uppercase tracking-[0.2em] py-4 px-8 hover:bg-[#232B40] hover:border-[rgba(200,169,106,0.8)] transition-colors duration-300"
              >
                View certificates
              </Link>
            </FadeUp>
            <FadeUp delay={1.1}>
              <p className="text-[10px] uppercase tracking-[0.3em] text-warning" data-testid="hero-disclaimer">
                {RUO}
              </p>
            </FadeUp>
          </div>

          <div className="lg:col-span-5 relative">
            <motion.div
              className="clipped-frame relative overflow-hidden border border-[rgba(200,169,106,0.35)] shadow-[0_0_90px_rgba(200,169,106,0.12)]"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            >
              <motion.img
                src={HERO_IMG}
                alt="Clinical research microscope"
                className="w-full max-h-[80vh] lg:max-h-none object-cover"
                style={{ y: imgY, scale: 1.15 }}
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-black/30" />
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
                <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-300">Fig. 01 — Analytical verification</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* MANIFESTO CHAPTERS */}
      <section className="border-t border-[#232B40]" data-testid="manifesto-section">
        <div className="mx-auto max-w-7xl px-6 sm:px-10 py-28 lg:py-40 space-y-32">
          <FadeUp>
            <p className="text-xs uppercase tracking-[0.35em] text-[#8E96AB]">The VetForge manifesto</p>
          </FadeUp>
          {CHAPTERS_LIVE.map((ch, i) => (
            <FadeUp key={ch.num} delay={0.1}>
              <div className={`grid grid-cols-1 lg:grid-cols-12 gap-10 items-start ${i % 2 === 1 ? "lg:text-right" : ""}`}>
                <div className={`lg:col-span-3 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                  <span className="font-serif text-7xl lg:text-8xl text-outline select-none" aria-hidden="true">{ch.num}</span>
                </div>
                <div className={`lg:col-span-9 space-y-6 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                  <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{ch.title}</h2>
                  <p className={`text-sm sm:text-base text-[#B9BFCE] leading-relaxed max-w-2xl ${i % 2 === 1 ? "lg:ml-auto" : ""}`}>{ch.body}</p>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* TRUST PILLARS */}
      {PILLARS.length > 0 && (
        <section className="border-t border-[#232B40] bg-[#0D1322]" data-testid="pillars-section">
          <div className="mx-auto max-w-7xl px-6 sm:px-10 py-20 lg:py-24">
            <FadeUp>
              <p className="text-xs uppercase tracking-[0.35em] text-clinical mb-12">Why researchers choose VetForge</p>
            </FadeUp>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#232B40] border border-[#232B40]">
              {PILLARS.map((p, i) => (
                <FadeUp key={i} delay={i * 0.08} className="bg-paper p-8 space-y-4">
                  <span className="font-stencil text-sm text-clinical" aria-hidden="true">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="font-serif text-xl tracking-tight text-ink" data-testid={`pillar-title-${i}`}>{p.title}</h3>
                  <p className="text-xs text-[#B9BFCE] leading-relaxed">{p.body}</p>
                </FadeUp>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* INSIDE VETFORGE CARDS */}
      {INSIDE.length > 0 && (
        <section className="border-t border-[#232B40]" data-testid="inside-cards-section">
          <div className="mx-auto max-w-7xl px-6 sm:px-10 py-20 lg:py-24">
            <FadeUp>
              <p className="text-xs uppercase tracking-[0.35em] text-clinical mb-3">Everything you need</p>
              <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink mb-12">What you'll find inside</h2>
            </FadeUp>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {INSIDE.map((c, i) => (
                <FadeUp key={i} delay={i * 0.08}>
                  <Link
                    to={c.link_url || "/"}
                    data-testid={`inside-card-${i}`}
                    className="group block h-full border border-[#232B40] bg-paper p-7 space-y-4 hover:border-[rgba(200,169,106,0.5)] hover:-translate-y-1 transition-all duration-300"
                  >
                    <h3 className="font-serif text-xl tracking-tight text-ink">{c.title}</h3>
                    <p className="text-xs text-[#B9BFCE] leading-relaxed">{c.body}</p>
                    <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-clinical">
                      {c.link_label || "Explore"}
                      <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </Link>
                </FadeUp>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FEATURED PRODUCTS */}
      {featured.length > 0 && (
        <section className="border-t border-[#232B40]" data-testid="featured-products-section">
          <div className="mx-auto max-w-7xl px-6 sm:px-10 py-28 lg:py-36">
            <FadeUp className="flex items-end justify-between gap-8 mb-20">
              <div className="space-y-4">
                <p className="text-xs uppercase tracking-[0.35em] text-clinical">{SECTIONS.featured_overline}</p>
                <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{SECTIONS.featured_title}</h2>
              </div>
              <Link
                to="/products"
                data-testid="featured-view-all-link"
                className="group hidden sm:inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#B9BFCE] hover:text-clinical transition-colors duration-300"
              >
                Full catalog
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </FadeUp>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#232B40] border border-[#232B40]">
              {featured.map((p, i) => {
                const st = PRODUCTS.find((s) => s.id === p.id) || {};
                return (
                  <FadeUp key={p.id} delay={i * 0.12}>
                    <Link
                      to="/products"
                      data-testid={`featured-product-${p.id}`}
                      className="group block bg-paper p-10 h-full space-y-8 hover:bg-surface transition-colors duration-500"
                    >
                      <div className="overflow-hidden rounded-xl bg-gradient-to-b from-[#1A233A] to-[#0D1322] border border-[#232B40] relative">
                        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(200,169,106,0.12),transparent_70%)] pointer-events-none" aria-hidden="true" />
                        <img
                          src={p.image || st.image || `/images/${p.id}.png`}
                          alt={p.name}
                          onError={(e) => { e.currentTarget.src = "/images/hero-vials.png"; }}
                          className="w-full aspect-square object-cover transition-transform duration-700 group-hover:scale-105 relative"
                        />
                      </div>
                      <div className="space-y-3">
                        <p className="text-[10px] uppercase tracking-[0.3em] text-[#8E96AB]">{st.category || "Peptide"} — {p.size || st.size}</p>
                        <span className="dog-tag dog-tag-sm text-xs" data-testid={`featured-tag-${p.id}`}>{p.name}</span>
                        {st.purity && <p className="text-xs text-clinical tracking-wide">{st.purity}</p>}
                      </div>
                    </Link>
                  </FadeUp>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="border-t border-[#232B40]" data-testid="home-cta-section">
        <div className="mx-auto max-w-7xl px-6 sm:px-10 py-28 lg:py-40 text-center space-y-10">
          <FadeUp>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-ink leading-none">
              {SECTIONS.cta_title1}<br /><span className="italic text-[#B9BFCE]">{SECTIONS.cta_title2}</span>
            </h2>
          </FadeUp>
          <FadeUp delay={0.15}>
            <p className="text-sm text-[#B9BFCE] max-w-xl mx-auto leading-relaxed">
              {SECTIONS.cta_intro}
            </p>
          </FadeUp>
          <FadeUp delay={0.25}>
            <div className="space-y-3" data-testid="payment-methods">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#8E96AB]">{SECTIONS.payment_label}</p>
              <div className="flex flex-wrap justify-center gap-3">
                {["PayPal", "Venmo", "CashApp", "Zelle"].map((m) => (
                  <span key={m} data-testid={`payment-${m.toLowerCase()}`} className="text-xs uppercase tracking-[0.2em] border border-[rgba(200,169,106,0.28)] px-5 py-2.5 text-ink">
                    {m}
                  </span>
                ))}
              </div>
              <p className="text-[11px] text-[#8E96AB] leading-relaxed max-w-xl mx-auto" data-testid="payment-fee-note">
                A 3.75% processing fee applies to PayPal and all card payments. Venmo, CashApp, and Zelle carry no fee. All sales are final.
              </p>
            </div>
          </FadeUp>
          <FadeUp delay={0.3}>
            <Link
              to="/support"
              data-testid="home-cta-button"
              className="group inline-flex items-center gap-3 bg-clinical text-[#0B101C] text-xs uppercase tracking-[0.2em] py-4 px-10 hover:bg-[#DBC28C] transition-colors duration-300"
            >
              Mission Support
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
